import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Forgot-password page.
 *
 * SECURITY: this page only POSTs the email. The reset token is delivered
 * exclusively through email — the API NEVER returns tokens, URLs, or any
 * indicator of whether the account exists. The success state shows the same
 * generic message regardless of input.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft size={14} />
          Back to sign in
        </Link>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 font-heading">Reset password</h1>
        <p className="mt-2 text-sm text-slate-600">
          Enter your account email. If the account exists, we'll email you a one-hour reset link.
        </p>

        {!submitted ? (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="forgot-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit-btn"
              className="w-full h-11 bg-blue-900 hover:bg-blue-800"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        ) : (
          <div className="mt-6 space-y-4" data-testid="forgot-result">
            <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-md">
              <EnvelopeSimple size={20} weight="duotone" className="text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700">
                If an account exists for <span className="font-medium text-slate-900">{email}</span>,
                we've sent a password-reset link to that email. The link expires in 1 hour.
                <div className="mt-2 text-xs text-slate-500">
                  Don't see it? Check your spam folder. Still nothing? Contact your school admin.
                </div>
              </div>
            </div>
            <Link
              to="/login"
              className="block w-full text-center text-sm font-medium text-blue-900 hover:underline"
              data-testid="back-to-login-link"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
